import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Box, Button, TextField, InputAdornment, useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { SimpleCustomTableProps, RowId, SimpleColumn, SimpleRow } from './interface';
import { nowOperationalDateTimeString } from '../../utils/operationalDateTime';
import { exportWatermark } from '../../utils/exportWatermark';
import { formatNumber } from '../../utils/format/currency';
import { useT } from '../../i18n';
import { shouldHideForbiddenActions } from '../../utils/permission-ui';


const normalizeColumnIdentifier = (value?: string | number | null) =>
  String(value ?? '').trim().toLowerCase();

const isActionLikeColumn = (column: SimpleColumn) => {
  const field = normalizeColumnIdentifier(column.field);
  const header = normalizeColumnIdentifier(column.headerName);
  const hasBlankHeader = header === '';
  const looksLikeOptionField =
    field.startsWith('opt_') ||
    field.startsWith('action_') ||
    field.endsWith('_action') ||
    field.endsWith('_actions');

  return (
    field === 'actions' ||
    field === 'options' ||
    field === 'opciones' ||
    header === 'opciones' ||
    header === 'acciones' ||
    looksLikeOptionField ||
    (hasBlankHeader && typeof column.renderCell === 'function')
  );
};

const hasRenderableNode = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || node === false || node === '') {
    return false;
  }

  if (Array.isArray(node)) {
    return node.some(hasRenderableNode);
  }

  if (React.isValidElement(node)) {
    const allowedProp = (node.props as { allowed?: unknown } | null)?.allowed;
    if (allowedProp === false && shouldHideForbiddenActions()) {
      return false;
    }

    if (node.type === React.Fragment) {
      return hasRenderableNode((node.props as { children?: React.ReactNode } | null)?.children);
    }

    return true;
  }

  return true;
};

const isSelectionDisabledRow = (row: SimpleRow) => Boolean((row as { disableSelection?: boolean } | null)?.disableSelection);

type InternalProps = SimpleCustomTableProps;

/** Búsqueda sin importar tildes ni mayúsculas. */
const normalizeSearch = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const SimpleCustomTable: React.FC<InternalProps> = ({
  rows,
  columns,
  enableSearch = false,
  searchPlaceholder,
  countValue,
  checkboxSelection = false,
  selectionModel = [],
  onRowSelectionModelChange,
  maxHeight = 400,
  disableInternalScroll = false,
  showBorders = true,
  showCount = true,
  exportCsv = true,
  exportFileName = 'export.csv',
  csvDelimiter = ';',
  wrapCellContent = false,
  autoRowHeight = false,
  columnGroups = [],
  rowHeight = 36,
  getRowCellSx,
}) => {
  const theme = useTheme();
  const t = useT();
  const [exporting, setExporting] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<RowId[]>(selectionModel);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setSelected(selectionModel);
  }, [selectionModel]);

  const disableVerticalScroll =
    disableInternalScroll === true ||
    disableInternalScroll === 'vertical' ||
    disableInternalScroll === 'all';
  const disableHorizontalScroll =
    disableInternalScroll === true ||
    disableInternalScroll === 'horizontal' ||
    disableInternalScroll === 'all';

  const visibleColumns = useMemo(() => {
    return columns.filter((column) => {
      if (!isActionLikeColumn(column)) {
        return true;
      }

      if (rows.length === 0) {
        return false;
      }

      return rows.some((row) => {
        if (column.renderCell) {
          return hasRenderableNode(column.renderCell({ row }));
        }

        return hasRenderableNode(row[column.field]);
      });
    });
  }, [columns, rows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalizeSearch(searchTerm.trim());
    if (!enableSearch || normalizedSearch === '') {
      return rows;
    }

    const searchableColumns = visibleColumns.filter((column) => !isActionLikeColumn(column));

    return rows.filter((row) =>
      searchableColumns.some((column) => {
        const rawValue = row[column.field];
        if (rawValue === null || rawValue === undefined) {
          return false;
        }

        return normalizeSearch(String(rawValue)).includes(normalizedSearch);
      })
    );
  }, [enableSearch, rows, searchTerm, visibleColumns]);

  // sin paginación: mostramos TODO
  const pageRows = useMemo(() => filteredRows, [filteredRows]);
  const effectiveRowsForHeader = pageRows.filter((row) => !isSelectionDisabledRow(row));
  const displayedCount = countValue ?? pageRows.length;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const addIds: RowId[] = effectiveRowsForHeader.map((r) => r.id);
      const setIds = new Set([...selected, ...addIds]);
      const newSelecteds = Array.from(setIds);
      setSelected(newSelecteds);
      onRowSelectionModelChange?.(newSelecteds);
      return;
    }
    const removeSet = new Set(effectiveRowsForHeader.map(r => r.id));
    const remaining = selected.filter(id => !removeSet.has(id));
    setSelected(remaining);
    onRowSelectionModelChange?.(remaining);
  };

  const handleSelectRow = (rowId: RowId) => {
    const exists = selected.includes(rowId);
    const newSelected = exists ? selected.filter(id => id !== rowId) : [...selected, rowId];
    setSelected(newSelected);
    onRowSelectionModelChange?.(newSelected);
  };

  const isSelected = (rowId: RowId) => selected.includes(rowId);

  const pageSelectedCount = effectiveRowsForHeader.reduce((acc, r) => acc + (selected.includes(r.id) ? 1 : 0), 0);
  const headerChecked = effectiveRowsForHeader.length > 0 && pageSelectedCount === effectiveRowsForHeader.length;
  const headerIndeterminate = pageSelectedCount > 0 && pageSelectedCount < effectiveRowsForHeader.length;

  // Construcción del CSV
  const escapeCsv = (val: string) => {
    if (!val) return '';
    const mustQuote = val.includes(csvDelimiter) || val.includes('"') || /\r|\n/.test(val);
    let s = val.replace(/"/g, '""');
    return mustQuote ? `"${s}"` : s;
  };

  const buildCsv = (watermark: string) => {
    const headers = visibleColumns.map(c => c.headerName ?? c.field);
    const headerLine = headers.map(escapeCsv).join(csvDelimiter);

    const bodyLines = rows.map(row => {
      const cells = visibleColumns.map(c => {
        const v = row[c.field];
        return escapeCsv(v != null ? String(v) : '');
      });
      return cells.join(csvDelimiter);
    });

    // Primera línea: marca de agua (quién exportó, cuándo y el número de registro).
    return '\uFEFF' + [escapeCsv(watermark), headerLine, ...bodyLines].join('\n'); // BOM para Excel
  };

  const downloadCsv = async () => {
    if (exporting) return;
    setExporting(true);
    const fileBaseName = exportFileName.replace(/\.csv$/i, '');
    const watermark = await exportWatermark(t, { fileName: `${fileBaseName}.csv`, rows: rows.length });
    setExporting(false);
    const csv = buildCsv(watermark);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = nowOperationalDateTimeString().replace(/[-: ]/g, '_');
    const fileBase = exportFileName.replace(/\.csv$/i, '');
    const fileNameWithDate = `${fileBase}_${timestamp}.csv`;

    link.href = url;
    link.setAttribute('download', fileNameWithDate);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };


  // Mismo aspecto que `DataTable` de @routes-plan/ui: encabezado sobre la superficie, texto en negrilla
  // suave y solo líneas horizontales; colores del tema para que se vea bien en claro y oscuro.
  const headerBg = theme.palette.background.paper;
  const lineColor = theme.palette.divider;
  const scroll = { thumb: alpha(theme.palette.text.primary, 0.28), track: 'transparent' };

  const tableStyles = useMemo(() => {
    const bodyCellHeightStyles = autoRowHeight
      ? {
        height: 'auto' as const,
        minHeight: `${rowHeight}px`,
        lineHeight: 'normal' as const,
        verticalAlign: 'top' as const,
      }
      : {
        height: `${rowHeight}px`,
        lineHeight: `${rowHeight - 5}px`,
      };

    const rowHeightStyles = autoRowHeight
      ? { height: 'auto' as const, minHeight: `${rowHeight}px` }
      : { height: `${rowHeight}px` };

    return {
      border: showBorders ? `1px solid ${lineColor}` : 'none',
      borderRadius: `${theme.shape.borderRadius}px`,
      backgroundColor: theme.palette.background.paper,
      '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: 'max-content',
        minWidth: '100%',
      },
      '& .MuiTableHead-root .MuiTableRow-root': { backgroundColor: headerBg },
      '& .MuiTableCell-head': {
        borderBottom: `1px solid ${lineColor}`,
        backgroundColor: headerBg,
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '0.8125rem',
        padding: '6px 12px',
        height: `${rowHeight}px`,
      },
      '& .MuiTableCell-body': {
        borderBottom: `1px solid ${lineColor}`,
        padding: '2px 12px',
        fontSize: '0.8125rem',
        ...bodyCellHeightStyles,
      },
      '& .MuiTableRow-root': {
        ...rowHeightStyles,
        '&:last-child .MuiTableCell-body': { borderBottom: 'none' },
        '&:hover': { backgroundColor: theme.palette.action.hover },
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.1),
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.32 : 0.18) },
        },
      },
      '& .MuiCheckbox-root': { padding: '4px' },
    };
  }, [showBorders, rowHeight, autoRowHeight, headerBg, lineColor, theme]);

  const columnGroupMeta = useMemo(() => {
    if (!columnGroups || columnGroups.length === 0) {
      return {
        hasGroups: false,
        fieldToGroup: new Map<string, string>(),
        groupToCount: new Map<string, number>(),
      };
    }

    const fieldToGroup = new Map<string, string>();
    const groupToCount = new Map<string, number>();
    const columnFieldSet = new Set(columns.map(col => col.field));

    columnGroups.forEach((group) => {
      let count = 0;
      group.fields.forEach((field) => {
        if (columnFieldSet.has(field)) {
          fieldToGroup.set(field, group.header);
          count += 1;
        }
      });
      if (count > 0) {
        groupToCount.set(group.header, count);
      }
    });

    return {
      hasGroups: groupToCount.size > 0,
      fieldToGroup,
      groupToCount,
    };
  }, [visibleColumns, columnGroups]);

  const buildCellSx = (column: SimpleColumn, shouldWrap: boolean) => ({
    width: column.width || 'auto',
    minWidth: column.minWidth || (column.width ? column.width : '80px'),
    maxWidth: column.maxWidth || 'none',
    whiteSpace: shouldWrap ? 'normal' : 'nowrap',
    overflow: 'hidden',
    textOverflow: shouldWrap ? 'unset' : 'ellipsis',
    ...(shouldWrap ? { wordBreak: 'break-word', overflowWrap: 'anywhere' } : {}),
  });

  const mergeHeaderCellSx = (column: SimpleColumn, shouldWrap: boolean): SxProps<Theme> =>
    [buildCellSx(column, shouldWrap), column.headerCellSx] as SxProps<Theme>;

  const mergeBodyCellSx = (column: SimpleColumn, shouldWrap: boolean, row: SimpleRow): SxProps<Theme> =>
    [
      buildCellSx(column, shouldWrap),
      getRowCellSx?.({ row }),
      column.getCellSx?.({ row }),
    ] as SxProps<Theme>;

  const selectionCellSx = { width: '60px', minWidth: '60px', maxWidth: '60px' } as const;
  const headerGroupCellSx = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '6px 12px',
    fontWeight: 600,
    backgroundColor: headerBg,
    color: theme.palette.text.primary,
  };

  const { hasGroups, fieldToGroup, groupToCount } = columnGroupMeta;

  const shouldRenderGroupSubHeaders = useMemo(() => {
    if (!hasGroups) {
      return false;
    }

    return visibleColumns.some((column) => {
      if (!fieldToGroup.has(column.field)) {
        return false;
      }
      const label = column.headerName !== undefined ? column.headerName : column.field;
      return (label ?? '').trim() !== '';
    });
  }, [hasGroups, visibleColumns, fieldToGroup]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Barra superior: cantidad de registros, búsqueda y exportar CSV. */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={1} mb={1.5}>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="nowrap" sx={{ minWidth: 0, flex: 1 }}>
          {showCount && (
            <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t(displayedCount === 1 ? 'common.recordCountOne' : 'common.recordCount', { count: formatNumber(displayedCount) })}
            </Box>
          )}
          {enableSearch && (
            <TextField
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={searchPlaceholder ?? t('common.searchPlaceholder')}
              inputProps={{ 'aria-label': t('common.search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 0, flex: 1, maxWidth: { sm: 360 } }}
            />
          )}
        </Box>
        {exportCsv && (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={isSmallScreen ? undefined : <FileDownloadOutlined />}
            onClick={downloadCsv}
            disabled={exporting}
            aria-label={t('common.exportCsv')}
            sx={isSmallScreen ? { minWidth: 40, width: 40, height: 40, p: 0, flexShrink: 0 } : { minHeight: 40, flexShrink: 0 }}
          >
            {isSmallScreen ? <FileDownloadOutlined fontSize="small" /> : t('common.exportCsv')}
          </Button>
        )}
      </Box>

      <TableContainer
        sx={{
          ...(!disableVerticalScroll ? { maxHeight } : {}),
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: disableHorizontalScroll ? 'visible' : 'auto',
          overflowY: disableVerticalScroll ? 'visible' : 'auto',
          scrollbarColor: `${scroll.thumb} ${scroll.track}`,
          '&::-webkit-scrollbar': {
            width: 14,
            height: 14,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: scroll.track,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: scroll.thumb,
            borderRadius: 999,
            border: `3px solid ${scroll.track}`,
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: scroll.track,
          },
          ...tableStyles,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            {hasGroups ? (
              <>
                <TableRow>
                  {checkboxSelection && (
                    <TableCell
                      padding="checkbox"
                      sx={selectionCellSx}
                      rowSpan={shouldRenderGroupSubHeaders ? 2 : 1}
                    >
                      <Checkbox
                        indeterminate={headerIndeterminate}
                        checked={headerChecked}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  {(() => {
                    const headerCells: React.ReactNode[] = [];
                    const processedGroups = new Set<string>();

                    visibleColumns.forEach((column) => {
                      const shouldWrap = column.wrapText ?? wrapCellContent;
                      const groupHeader = fieldToGroup.get(column.field);
                      const headerContent = column.renderHeader ? column.renderHeader() : (column.headerName ?? column.field);

                      if (groupHeader) {
                        if (!processedGroups.has(groupHeader)) {
                          const colSpan = groupToCount.get(groupHeader) ?? 0;
                          if (colSpan > 0) {
                            headerCells.push(
                              <TableCell
                                key={`group-${groupHeader}`}
                                align="center"
                                colSpan={colSpan}
                                sx={headerGroupCellSx}
                              >
                                {groupHeader}
                              </TableCell>
                            );
                          }
                          processedGroups.add(groupHeader);
                        }
                      } else {
                        headerCells.push(
                          <TableCell
                            key={column.field}
                            align={column.align || 'left'}
                            rowSpan={shouldRenderGroupSubHeaders ? 2 : 1}
                            sx={mergeHeaderCellSx(column, shouldWrap)}
                          >
                            {headerContent}
                          </TableCell>
                        );
                      }
                    });

                    return headerCells;
                  })()}
                </TableRow>
                {shouldRenderGroupSubHeaders && (
                  <TableRow>
                    {visibleColumns.map((column) => {
                      const groupHeader = fieldToGroup.get(column.field);
                      if (!groupHeader) {
                        return null;
                      }
                      const shouldWrap = column.wrapText ?? wrapCellContent;
                      const headerContent = column.renderHeader ? column.renderHeader() : (column.headerName ?? column.field);
                      return (
                        <TableCell
                          key={column.field}
                          align={column.align || 'left'}
                          sx={mergeHeaderCellSx(column, shouldWrap)}
                        >
                          {headerContent}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                {checkboxSelection && (
                  <TableCell padding="checkbox" sx={selectionCellSx}>
                    <Checkbox
                      indeterminate={headerIndeterminate}
                      checked={headerChecked}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                {visibleColumns.map((column) => {
                  const shouldWrap = column.wrapText ?? wrapCellContent;
                  const headerContent = column.renderHeader ? column.renderHeader() : (column.headerName ?? column.field);
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      sx={mergeHeaderCellSx(column, shouldWrap)}
                    >
                      {headerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {pageRows.map((row) => {
              const isItemSelected = isSelected(row.id);
              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={isItemSelected}
                  role="checkbox"
                  aria-checked={isItemSelected}
                >
                  {checkboxSelection && (
                    <TableCell padding="checkbox" sx={selectionCellSx}>
                      {isSelectionDisabledRow(row as SimpleRow) ? null : (
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectRow(row.id)}
                        />
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.map((column) => {
                    const shouldWrap = column.wrapText ?? wrapCellContent;
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        sx={mergeBodyCellSx(column, shouldWrap, row as SimpleRow)}
                      >
                        {column.renderCell ? column.renderCell({ row }) : row[column.field]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SimpleCustomTable;
